import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Chip } from '@mui/material';
import { GridSchema, GridColumnSchema } from './grid.types';
import { TableHeader } from './table.types';
import { useDynamicFilterData } from './useDynamicFilterData';
import { transformToTreeStructure } from '@/utils/treeUtils';
import { useDebounce } from '@/hooks/useDebounce';

// Status color mapping for badge cell types — matches the previous
// bulk-upload chip palette (see bulk-upload.module.scss .statusChip*).
const getStatusBadgeProps = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'success':
      return {
        color: 'primary.main',
        backgroundColor: '#B8FBF0',
        label: 'completed'
      };
    case 'partial_success':
      return {
        color: '#FF6F00',
        backgroundColor: '#FFF3E0',
        label: 'partial_success'
      };
    case 'processing':
      return {
        color: '#e65100',
        backgroundColor: '#FFE082',
        label: 'in_progress'
      };
    case 'pending':
      return {
        color: '#576473',
        backgroundColor: '#EFF3F6',
        label: 'pending'
      };
    case 'failed':
    case 'failure':
    case 'error':
      return {
        color: '#9D0F41',
        backgroundColor: '#FFECFC',
        label: 'failed'
      };
    default:
      return {
        color: '#757575',
        backgroundColor: '#f5f5f5',
        label: String(status)
      };
  }
};

type OnFilterChange = {
  (key: string, value: any): void;
  (updates: Record<string, any>): void;
};

interface UseSchemaToHeadersOptions {
  schema: GridSchema | null;
  onFilterChange?: OnFilterChange;
  filterValues?: Record<string, any>;
  visibilityOverrides?: Record<string, boolean>; // User preference visibility overrides
}

export const useSchemaToHeaders = ({
  schema,
  onFilterChange,
  filterValues = {},
  visibilityOverrides = {},
}: UseSchemaToHeadersOptions) => {
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});

  // Stable ref so closures inside useMemo always call the latest onFilterChange
  // without needing it as a memo dependency
  const onFilterChangeRef = useRef(onFilterChange);
  useEffect(() => { onFilterChangeRef.current = onFilterChange; }, [onFilterChange]);

  // Local state for text/number filter inputs — updated immediately on keystroke
  const [localTextValues, setLocalTextValues] = useState<Record<string, string>>({});

  // Debounced version — only this gets written to the URL
  const debouncedTextValues = useDebounce(localTextValues, 500);

  // Track which keys we've already synced from URL on mount
  const syncedFromUrlRef = useRef<Set<string>>(new Set());

  // Sync from URL into local state only on first mount per filter key,
  // or when filterValues is externally cleared (value becomes empty while local is not)
  useEffect(() => {
    const textColumns = schema?.columns.filter(
      (col) => col.filter_type === 'text' || col.filter_type === 'number' ||
               col.filter_type === 'json_text' || col.filter_type === 'json_number'
    ) ?? [];

    setLocalTextValues((prev) => {
      const next = { ...prev };
      let changed = false;

      textColumns.forEach((col) => {
        const isFK = col.relation_type === 'fk' || col.relation_type === 'o2o' || col.relation_type === 'o2m';
        const isJsonSelectType = col.filter_type === 'json_select' || col.filter_type === 'json_multiselect';
        const operators = col.filter_config?.operators as string[] | undefined;
        const operator = operators?.[0];
        let paramKey 
        if (col.filter_type === 'text' && !operators) {
          paramKey = `${col.filter_key}__${'icontains'}`;
        }else{
          paramKey = (isFK || isJsonSelectType) ? (col.filter_key ?? col.key) : `${col.filter_key}__${operator ?? 'icontains'}`;
        }
        const urlVal = filterValues[paramKey] ?? '';
        const localVal = prev[col.key] ?? '';

        if (!syncedFromUrlRef.current.has(col.key)) {
          syncedFromUrlRef.current.add(col.key);
          if (urlVal !== localVal) {
            next[col.key] = String(urlVal);
            changed = true;
          }
        } else if ((urlVal === '' || urlVal === null || urlVal === undefined) && localVal !== '') {
          // Clear local value when URL value is empty, null, or undefined
          next[col.key] = '';
          changed = true;
        } else if (urlVal !== '' && urlVal !== localVal) {
          // Update local value when URL value changes to a non-empty value
          next[col.key] = String(urlVal);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  // Write debounced text values to URL
  const prevDebouncedTextRef = useRef<Record<string, string>>({});
  useEffect(() => {
    Object.entries(debouncedTextValues).forEach(([colKey, value]) => {
      const prev = prevDebouncedTextRef.current[colKey];
      if (value !== prev) {
        prevDebouncedTextRef.current[colKey] = value;
        const col = schema?.columns.find((c) => c.key === colKey);
        if (col?.filter_key) {
          const isFK = col.relation_type === 'fk' || col.relation_type === 'o2o' || col.relation_type === 'o2m';
          const isJsonSelectType = col.filter_type === 'json_select' || col.filter_type === 'json_multiselect';
          const operators = col.filter_config?.operators as string[] | undefined;
          const operator = operators?.[0];
          let paramKey: string;
          if (col.filter_type === 'text' && !operators) {
            paramKey = `${col.filter_key}__${'icontains'}`;
          }else{
            paramKey = (isFK || isJsonSelectType) ? col.filter_key : `${col.filter_key}__${operator ?? 'icontains'}`;
          }
          onFilterChangeRef.current?.(paramKey, value || null);
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTextValues]);

  const stableSchema = useMemo(
    () => schema?.columns || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(schema?.columns?.map((c) => c.key))]
  );

  // Create a filtered schema that includes visibility overrides for useDynamicFilterData
  const visibleSchema = useMemo(() => {
    return stableSchema.filter((col) => {
      // Use visibility override if available, otherwise use schema visibility
      const isVisible = visibilityOverrides[col.key] !== undefined
        ? visibilityOverrides[col.key]
        : col.visible;
      return isVisible;
    });
  }, [stableSchema, visibilityOverrides]);

  const {
    getFilterOptions,
    isFilterLoading,
    hasMoreOptions,
    loadMoreOptions,
  } = useDynamicFilterData({ schema: visibleSchema, searchValues });

  const handleSearchChange = useCallback((columnKey: string, searchValue: string) => {
    setSearchValues((prev) => ({ ...prev, [columnKey]: searchValue }));
  }, []);

  const headers: TableHeader<any>[] = useMemo(() => {
    if (!stableSchema.length) return [];

    return [...stableSchema]
      .sort((a, b) => a.order - b.order)
      .filter((col) => {
        // Use visibility override if available, otherwise use schema visibility
        const isVisible = visibilityOverrides[col.key] !== undefined
          ? visibilityOverrides[col.key]
          : col.visible;
        return isVisible;
      })
      .map((column: GridColumnSchema) => {
        const header: TableHeader<any> = {
          key: column.key,
          label: column.label,
          sortable: !!column.sort_key,
          sortKey: column.sort_key,
          width: column.width,
        };

        if (column.filter_type && column.filter_key) {
          const isFK = column.relation_type === 'fk' || column.relation_type === 'o2o' || column.relation_type === 'o2m';
          const isJsonSelectType = column.filter_type === 'json_select' || column.filter_type === 'json_multiselect';
          // select_choice is a CharField-with-choices select. The runtime
          // filter backend registers it as a CharInFilter under the bare
          // filter_key (no operator suffix) — so the URL param must omit the
          // suffix too, same as FK / json_select.
          const isStaticChoiceSelect = column.filter_type === 'select_choice';
          const operators = column.filter_config?.operators as string[] | undefined;
          const defaultOperator = operators?.[0];

          // For FK fields, json_select/json_multiselect, and select_choice
          // fields, use filter_key directly without operator. For other
          // fields, the URL param key is filter_key__operator.
          let paramKey: string;
          if (column.filter_type === 'text' && !operators) {
            paramKey = `${column.filter_key}__${'icontains'}`;
          } else {
            paramKey =
              isFK || isJsonSelectType || isStaticChoiceSelect
                ? column.filter_key
                : `${column.filter_key}__${defaultOperator ?? 'icontains'}`;
          }

          switch (column.filter_type) {
            case 'text':
            case 'json_text':
              header.type = 'text';
              header.filterValue = localTextValues[column.key] ?? '';
              header.onFilterChange = (value) => {
                const str = value == null ? '' : String(value);
                setLocalTextValues((prev) => ({ ...prev, [column.key]: str }));
              };
              break;

            case 'date':
            case 'json_date':
              header.type = 'datePicker';
              // date uses gte/lte operators — map to two separate params
              header.filterValue = [
                filterValues[`${column.filter_key}__gte`] ?? '',
                filterValues[`${column.filter_key}__lte`] ?? '',
              ];
              header.onFilterChange = (value) => {
                const [from, to] = value as [string, string];
                // Convert empty strings to null for proper API handling
                onFilterChangeRef.current?.({
                  [`${column.filter_key}__gte`]: from === '' ? null : from,
                  [`${column.filter_key}__lte`]: to === '' ? null : to,
                });
              };
              break;

            case 'select':
            case 'select_choice':
            case 'dropdown':
            case 'json_select': {
              header.type = 'dropdown';
              header.filterMultiple = column.filter_config?.multiple ?? false;
              const rawVal = filterValues[paramKey];
              // URL always stores as comma-separated string — split back to array for multi
              header.filterValue = header.filterMultiple
                ? (rawVal ? String(rawVal).split(',').filter(Boolean) : [])
                : (rawVal ?? '');
              header.onFilterChange = (value) => {
                // Write array as comma-separated string to URL
                const urlValue = Array.isArray(value)
                  ? (value.length ? value.join(',') : null)
                  : (value || null);
                onFilterChangeRef.current?.(paramKey, urlValue);
              };

              if (column.filter_config?.async) {
                const filterOptions = getFilterOptions(column.key) || [];
                header.dropdownOptions = filterOptions.map((o) => ({
                  label: o.label,
                  value: String(o.value),
                }));
                header.filterLoading = isFilterLoading(column.key);
                header.filterInputValue = searchValues[column.key] || '';
                header.onFilterInputChange = (value: string) =>
                  handleSearchChange(column.key, value);
                header.onFilterScrollEnd = () => {
                  if (hasMoreOptions(column.key)) loadMoreOptions(column.key);
                };
              } else if (Array.isArray(column.filter_config?.options)) {
                header.dropdownOptions = (column.filter_config!.options as any[]).map((o) => ({
                  label: String(o.label ?? o.name ?? o.value),
                  value: String(o.value ?? o.id),
                }));
              }
              break;
            }

            case 'tree': {
              header.type = 'tree';
              header.filterMultiple = column.filter_config?.multiple ?? false;
              const rawTreeVal = filterValues[column.filter_key];
              // URL always stores as comma-separated string — split back to array
              header.filterValue = rawTreeVal
                ? String(rawTreeVal).split(',').filter(Boolean)
                : [];
              header.onFilterChange = (value) => {
                const urlValue = Array.isArray(value)
                  ? (value.length ? value.join(',') : null)
                  : (value || null);
                onFilterChangeRef.current?.(column.filter_key!, urlValue);
              };

              if (column.filter_config?.async) {
                const flatOptions = getFilterOptions(column.key) || [];
                header.treeOptions = transformToTreeStructure(
                  flatOptions.map((o) => ({
                    id: String(o.value),
                    name: o.label,
                    parent_category: (o as any).parent_category || null,
                    parent_location: (o as any).parent_location || null,
                    parent_asset: (o as any).parent_asset || null,
                  }))
                );
                header.filterLoading = isFilterLoading(column.key);
                header.filterSearchValue = searchValues[column.key] || '';
                header.onFilterSearchChange = (value: string) =>
                  handleSearchChange(column.key, value);
              }
              break;
            }

            case 'number':
            case 'json_number': {
              // Check if this number field has range operators (gte/lte)
              const operators = column.filter_config?.operators as string[] | undefined;
              const hasRangeOperators = operators?.includes('gte') && operators?.includes('lte');
              
              if (hasRangeOperators) {
                // Use range filter similar to date filters
                header.type = 'numberRange';
                header.filterValue = [
                  filterValues[`${column.filter_key}__gte`] ?? '',
                  filterValues[`${column.filter_key}__lte`] ?? '',
                ];
                header.onFilterChange = (value) => {
                  const [from, to] = value as [string, string];
                  // Convert empty strings to null for proper API handling
                  onFilterChangeRef.current?.({
                    [`${column.filter_key}__gte`]: from === '' ? null : from,
                    [`${column.filter_key}__lte`]: to === '' ? null : to,
                  });
                };
              } else {
                // Use simple text filter for exact matches
                header.type = 'text';
                header.filterValue = localTextValues[column.key] ?? '';
                header.onFilterChange = (value) => {
                  const str = value == null ? '' : String(value);
                  setLocalTextValues((prev) => ({ ...prev, [column.key]: str }));
                };
              }
              break;
            }

            case 'boolean':
              header.type = 'select';
              header.filterValue = filterValues[column.filter_key] ?? '';
              header.onFilterChange = (value) => onFilterChangeRef.current?.(column.filter_key!, value);
              header.dropdownOptions = [
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ];
              header.filterAllLabel = 'All';
              break;

            case 'json_multiselect': {
              header.type = 'dropdown';
              header.filterMultiple = column.filter_config?.multiple ?? true;
              const rawMultiVal = filterValues[paramKey];
              header.filterValue = rawMultiVal
                ? String(rawMultiVal).split(',').filter(Boolean)
                : [];
              header.onFilterChange = (value) => {
                const urlValue = Array.isArray(value)
                  ? (value.length ? value.join(',') : null)
                  : (value || null);
                onFilterChangeRef.current?.(paramKey, urlValue);
              };

              if (column.filter_config?.async) {
                const filterOptions = getFilterOptions(column.key) || [];
                header.dropdownOptions = filterOptions.map((o) => ({
                  label: o.label,
                  value: String(o.value),
                }));
                header.filterLoading = isFilterLoading(column.key);
                header.filterInputValue = searchValues[column.key] || '';
                header.onFilterInputChange = (value: string) =>
                  handleSearchChange(column.key, value);
                header.onFilterScrollEnd = () => {
                  if (hasMoreOptions(column.key)) loadMoreOptions(column.key);
                };
              } else if (Array.isArray(column.filter_config?.options)) {
                header.dropdownOptions = (column.filter_config!.options as any[]).map((o) => ({
                  label: String(o.label ?? o.name ?? o.value),
                  value: String(o.value ?? o.id),
                }));
              }
              break;
            }
          }

          if (column.filter_config) {
            header.menuWidth = 320;
            header.minMenuWidth = 260;
            header.maxMenuWidth = 360;
          }
        }

        // Render function based on cell_type
        header.render = (row: any) => {
          const value = getNestedValue(row, column.key);

          if (value === null || value === undefined || value === '') return '-';

          // Handle arrays (like checkbox fields with multiple selections)
          if (Array.isArray(value)) {
            if (value.length === 0) return '-';
            
            // Extract labels from array of objects
            const labels = value.map(item => {
              if (typeof item === 'object' && item !== null) {
                return item.label || item.name || String(item);
              }
              return String(item);
            }).filter(Boolean);
            
            return labels.length > 0 ? labels.join(', ') : '-';
          }

          // Handle single objects (like radio button selections)
          if (typeof value === 'object' && value !== null) {
            if (value.label) return String(value.label);
            if (value.name) return String(value.name);
            return String(value);
          }

          // Handle badge cell type with color coding
          if (column.cell_type === 'badge') {
            // Special handling for status field
            if (column.key === 'status') {
              const badgeProps = getStatusBadgeProps(String(value));
              return React.createElement(Chip, {
                label: badgeProps.label,
                size: 'small',
                sx: {
                  color: badgeProps.color,
                  backgroundColor: badgeProps.backgroundColor,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: '24px',
                  '& .MuiChip-label': {
                    paddingX: '8px',
                  },
                },
              });
            }
            
            // Handle other badge types with filter_config options
            if (column.filter_config?.options && Array.isArray(column.filter_config.options)) {
              const option = column.filter_config.options.find((opt: any) =>
                String(opt.value) === String(value)
              );
              if (option) {
                return String(option.label);
              }
            }
            
            // Fallback for badge without options
            return String(value);
          }

          // Handle select/dropdown cell types with filter_config options
          if ((column.cell_type === 'select' || column.cell_type === 'dropdown') && column.filter_config?.options && Array.isArray(column.filter_config.options)) {
            const option = column.filter_config.options.find((opt: any) =>
              String(opt.value) === String(value)
            );
            if (option) {
              return String(option.label);
            }
          }

          switch (column.cell_type) {
            case 'number':
              return typeof value === 'number' ? value.toLocaleString('en-US') : String(value);
            case 'date':
              return value ? new Date(value).toLocaleDateString() : '-';
            case 'datetime':
              return value ? new Date(value).toLocaleString() : '-';
            case 'boolean':
              return value ? 'Yes' : 'No';
            default:
              return String(value);
          }
        };

        return header;
      });
  // onFilterChange is intentionally excluded — it's only used in the debounce
  // effect above, not inside this memo. Including it would cause headers to
  // recompute on every URL change, resetting activeFilterKey and closing the input.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stableSchema,
    localTextValues,
    filterValues,
    visibilityOverrides,
    getFilterOptions,
    isFilterLoading,
    hasMoreOptions,
    loadMoreOptions,
    searchValues,
    handleSearchChange,
  ]);

  return { headers, searchValues, setSearchValues };
};

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}
